import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/store/AuthStore";
import { AppProvider } from "@/store/AppStore";
import { TopBar } from "@/components/TopBar";
import { BottomNav } from "@/components/BottomNav";
import { LoginPage } from "@/pages/LoginPage";
import { CalcPage } from "@/pages/CalcPage";
import { AssemblyPage } from "@/pages/AssemblyPage";
import { OrderPage } from "@/pages/OrderPage";
import { CostingPage } from "@/pages/CostingPage";
import { RatePage } from "@/pages/RatePage";
import { Rate88Page } from "@/pages/Rate88Page";
import { InventoryPage } from "@/pages/InventoryPage";
import { ImportOrderPage } from "@/pages/ImportOrderPage";
import StockPage from "@/pages/StockPage";
import { TablesPage } from "@/pages/TablesPage";

const ALL_TABS = ["calc", "assembly", "order", "costing", "rate", "rate88", "import", "stock", "tables"];

if (typeof navigator !== "undefined" && navigator.serviceWorker) {
  navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
}

function Shell() {
  const { user } = useAuth();
  const allowed = !user ? ["calc"] : user.role === "admin" ? ALL_TABS : (user.permissions || ALL_TABS);
  const [tab, setTab] = useState("calc");
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    if (!allowed.includes(tab)) setTab(allowed[0] || "calc");
  }, [allowed, tab]);

  useEffect(() => {
    if (user) setShowLogin(false);
  }, [user]);

  return (
    <AppProvider>
      <div className="min-h-screen bg-[#090D16] bg-grid text-slate-100">
        <div>
          <TopBar onLogin={() => setShowLogin(true)} />
          <main className="mx-auto max-w-3xl px-4 pb-28 pt-4 animate-fade-up" key={tab}>
            {tab === "calc" && <CalcPage />}
            {tab === "assembly" && <AssemblyPage />}
            {tab === "order" && <OrderPage />}
            {tab === "costing" && <CostingPage />}
            {tab === "rate" && <RatePage />}
            {tab === "rate88" && <Rate88Page />}
            {tab === "import" && <ImportOrderPage />}
            {tab === "stock" && <StockPage />}
            {tab === "tables" && <TablesPage />}
          </main>
          <BottomNav active={tab} onChange={setTab} />
        </div>
        {showLogin && !user && (
          <div className="fixed inset-0 z-[60]">
            <LoginPage onClose={() => setShowLogin(false)} />
          </div>
        )}
      </div>
    </AppProvider>
  );
}

function Gate() {
  const { ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#090D16]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }
  return <Shell />;
}

function App() {
  return (
    <AuthProvider>
      <Gate />
      <Toaster position="top-center" richColors theme="dark" />
    </AuthProvider>
  );
}

export default App;
